import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { agregarTarea } from "../../../features/registroTareasSlice";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Material UI y Lucide
import { 
  Box, Typography, TextField, Grid, Button, Paper, Stack 
} from "@mui/material";
import { PlusCircle, ReceiptText, Calendar, Users, Briefcase } from "lucide-react";

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const PALETTE = { 
  ice: "#C2EDF0", 
  aqua: "#00C0CA", 
  teal: "#008B9C", 
  dark: "#22404D", 
  slate: "#94A3B8",
  pearl: "#EEF3F4"
};

const SolicitudPresupuesto = ({ idPedido }) => {
    // ==========================================
    // LÓGICA ORIGINAL (MANTENIDA)
    // ==========================================
    const tareas = useSelector(state => state.tareas.Tareas);
    const dispatch = useDispatch();
    const [formSolicitudPresupuesto, setFormSolicitudPresupuesto] = useState({
        tipoTarea: "SolicitudPresupuesto",
        tipo: "",
        fecha: "",
        cantidadPersonas: "",
        estado: "pendiente",
    });

    const [mostrarFormulario, setMostrarFormulario] = useState(true);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormSolicitudPresupuesto({
            ...formSolicitudPresupuesto,
            [name]: value
        });
    };

    const validar = () => {
        const { tipo, fecha, cantidadPersonas } = formSolicitudPresupuesto;
        const fechaFormulario = new Date(fecha);
        const fechaActual = new Date();
        fechaActual.setHours(0, 0, 0, 0);
        fechaFormulario.setHours(0, 0, 0, 0);

        if (!tipo) return "El tipo no puede estar vacío.";
        if (!fecha) return "La fecha no puede estar vacía.";
        if (fechaFormulario < fechaActual) return "La fecha debe ser una fecha futura.";
        if (!cantidadPersonas || isNaN(cantidadPersonas) || cantidadPersonas <= 0) {
            return "La cantidad de personas debe ser un número mayor a 0.";
        }
        return ""; 
    };

    const agregarTareaAlPedido = async () => {
        const mensaje = validar();
        if (mensaje !== "") {
            toast.error(mensaje, { position: "bottom-center", autoClose: 3000 });
            return;
        }

        const { tipo, fecha, cantidadPersonas, estado } = formSolicitudPresupuesto;
        const registro = {           
            id: tareas.length + 1,
            tipoTarea: "SolicitudPresupuesto",
            tipo,
            fecha,
            comentario:"",
            cantidadPersonas: parseInt(cantidadPersonas, 10),
            estado,
        };

        if (idPedido) {
            const respuesta = await fetch(`${apiUrl}/Tarea/AgregarTareaSolicitudPresupuesto?IdPedido=` + idPedido, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'apiKey': localStorage.getItem("apiKey") 
                },
                body: JSON.stringify(registro)
            });

            if (respuesta.ok) {
                setMostrarFormulario(false);
                toast.success('Tarea agregada correctamente', { position: "bottom-center", autoClose: 3000 });
            }
        } else {
            dispatch(agregarTarea(registro));
            setMostrarFormulario(false);
            toast.success('Tarea agregada correctamente', { position: "bottom-center", autoClose: 3000 });
        }
    };
    // ==========================================

    if (!mostrarFormulario) return null;

    return (
        <Paper 
            elevation={0} 
            sx={{ 
                p: 3, 
                borderRadius: '24px', 
                bgcolor: PALETTE.pearl, 
                border: `1px solid ${PALETTE.ice}`,
                mt: 2,
                animation: 'fadeIn 0.3s ease-in-out'
            }}
        >
            <Stack spacing={2.5}>
                <Box display="flex" alignItems="center" gap={1}>
                    <ReceiptText size={20} color={PALETTE.teal} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: PALETTE.dark }}>
                        Solicitud de Presupuesto
                    </Typography>
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>TIPO DE PRESUPUESTO</Typography>
                        <TextField 
                            fullWidth 
                            name="tipo" 
                            value={formSolicitudPresupuesto.tipo} 
                            onChange={handleChange} 
                            placeholder="Ej: Servicio de Catering, Merchandising"
                            InputProps={{ startAdornment: <Briefcase size={16} style={{marginRight: 8, color: PALETTE.slate}} /> }}
                            sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: 'white' } }} 
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>FECHA ESTIMADA</Typography>
                        <TextField 
                            fullWidth 
                            type="date" 
                            name="fecha" 
                            value={formSolicitudPresupuesto.fecha} 
                            onChange={handleChange} 
                            inputProps={{ min: new Date().toISOString().split('T')[0] }}
                            sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: 'white' } }} 
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>CANT. PERSONAS</Typography>
                        <TextField 
                            fullWidth 
                            type="number" 
                            name="cantidadPersonas" 
                            value={formSolicitudPresupuesto.cantidadPersonas} 
                            onChange={handleChange} 
                            InputProps={{ startAdornment: <Users size={16} style={{marginRight: 8, color: PALETTE.slate}} /> }}
                            sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: 'white' } }} 
                        />
                    </Grid>
                </Grid>

                <Button 
                    variant="contained" 
                    fullWidth 
                    onClick={agregarTareaAlPedido}
                    startIcon={<PlusCircle size={18} />}
                    sx={{ 
                        mt: 1, 
                        borderRadius: '12px', 
                        py: 1.2, 
                        fontWeight: 800, 
                        bgcolor: PALETTE.teal,
                        '&:hover': { bgcolor: PALETTE.dark }
                    }}
                >
                    Añadir Solicitud
                </Button>
            </Stack>
        </Paper>
    );
};

export default SolicitudPresupuesto;